/**
 * GitHub API client (frontend → tile-backend → GitHub).
 *
 * Stage 4 of the GitHub workspace rollout.
 */

import type { GitHubConfig, PullRequest } from '@/shared/types/releases.types';

async function call<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = await res.text();
    let message = body;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      message = parsed.error || body;
    } catch {
      /* keep raw body */
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export interface GitHubBranchListItem {
  name: string;
  sha: string;
  isProtected: boolean;
  isDefault: boolean;
}

export interface UserRepoListItem {
  fullName: string;
  owner: string;
  repo: string;
  private: boolean;
  defaultBranch: string;
  description: string | null;
  pushedAt: string;
}

export const githubApi = {
  getInstallUrl: (appId: string, returnTo?: string) => {
    const qs = new URLSearchParams({ appId });
    if (returnTo) qs.set('returnTo', returnTo);
    return call<{ url: string }>(`/api/github/install-url?${qs}`);
  },

  /** SPA-driven OAuth exchange. Called by /github-callback page. */
  exchange: (code: string, state: string) =>
    call<{ appId: string; githubLogin: string; alreadyConnected: boolean }>(
      `/api/github/exchange`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
      },
    ),

  getConfig: (appId: string) => call<GitHubConfig>(`/api/apps/${encodeURIComponent(appId)}/github`),

  userRepos: (appId: string, page = 1, perPage = 100) =>
    call<{ repos: UserRepoListItem[]; page: number; perPage: number; hasMore: boolean }>(
      `/api/apps/${encodeURIComponent(appId)}/github/user-repos?page=${page}&perPage=${perPage}`,
    ),

  connectRepo: (appId: string, owner: string, repo: string) =>
    call<{ connected: boolean; repo: string; defaultBranch: string }>(
      `/api/apps/${encodeURIComponent(appId)}/github/connect-repo`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner, repo }),
      },
    ),

  disconnect: (appId: string) =>
    call<{ success: true }>(`/api/apps/${encodeURIComponent(appId)}/github/disconnect`, {
      method: 'POST',
    }),

  listBranches: (appId: string, page = 1, perPage = 100) =>
    call<{ branches: GitHubBranchListItem[]; page: number; perPage: number; hasMore: boolean }>(
      `/api/apps/${encodeURIComponent(appId)}/github/branches?page=${page}&perPage=${perPage}`,
    ),

  getBranch: (appId: string, name: string) =>
    call<{
      name: string;
      sha: string;
      message: string;
      author: string;
      authorLogin?: string;
      committedAt: string;
    }>(`/api/apps/${encodeURIComponent(appId)}/github/branches/${encodeURIComponent(name)}`),

  /**
   * Drift check: does the latest cloud save's base_git_sha for this
   * branch match the current GitHub HEAD?
   *
   * Called from openBranch BEFORE swapping files. If hasDrift is true,
   * the FE shows BranchDriftModal instead of proceeding with the swap.
   */
  getBranchDrift: (appId: string, branch: string) =>
    call<{
      hasDrift: boolean;
      reason: 'in_sync' | 'no_cloud_saves' | 'git_advanced' | 'git_branch_gone';
      cloudBaseSha: string | null;
      currentGitSha: string | null;
      unpushedSaveCount: number;
      latestSaveAt: string | null;
    }>(`/api/apps/${encodeURIComponent(appId)}/branches/${encodeURIComponent(branch)}/drift`),

  /**
   * Materialize a branch as a cloud save. Read-through cache:
   * if a cloud copy exists for the current git HEAD, it's returned;
   * otherwise files are fetched from git and a fresh save is created.
   *
   * The editor's openBranch flow calls this instead of hitting git
   * directly so that every branch open lands the files in our cloud,
   * and repeat opens at the same commit cost zero GitHub calls.
   */
  materializeBranch: (appId: string, branch: string) =>
    call<{
      saveId: string;
      baseGitSha: string;
      cached: boolean;
    }>(
      `/api/apps/${encodeURIComponent(appId)}/branches/${encodeURIComponent(branch)}/materialize`,
      {
        method: 'POST',
      },
    ),

  createBranch: (appId: string, name: string, fromBranch: string) =>
    call<{
      name: string;
      sha: string;
      fromBranch: string;
      isDefault: boolean;
      isProtected: boolean;
    }>(`/api/apps/${encodeURIComponent(appId)}/github/branches`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, fromBranch }),
    }),

  pushNow: (appId: string, opts: { branch?: string; message?: string } = {}) =>
    call<{
      success: boolean;
      gitSha: string;
      saveId: string;
      branch: string;
      repo: string;
      message: string;
      fileCount: number;
    }>(`/api/apps/${encodeURIComponent(appId)}/github/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    }),

  listPullRequests: (appId: string, state: 'open' | 'closed' | 'all' = 'open') =>
    call<{ pullRequests: PullRequest[] }>(
      `/api/apps/${encodeURIComponent(appId)}/github/pulls?state=${state}`,
    ),
};
